/**
 * ============================================================================
 * [P1-02] MainLayout Component
 *
 * 애플리케이션 메인 레이아웃 컴포넌트
 *
 * 구조:
 * - OfflineIndicator (상단 고정)
 * - Container
 *   - Header (타이틀 + 인증)
 *   - Main (children 또는 Outlet)
 *   - Footer (선택적)
 *
 * Safety:
 * - CSS Module 사용으로 기존 style.css와 충돌 방지
 * ============================================================================
 */

import type { ReactNode } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { Header } from './Header'
import { OfflineIndicator } from './OfflineIndicator'
import styles from './Layout.module.css'

// ============================================================================
// 타입 정의
// ============================================================================
interface MainLayoutProps {
  children: ReactNode
  showFooter?: boolean
}

// ============================================================================
// [P1-02] MainLayout 컴포넌트
// ============================================================================
export function MainLayout({ children, showFooter = false }: MainLayoutProps) {
  // ========================================================================
  // Auth Hook 사용
  // ========================================================================
  const {
    user,
    isLoading,
    loginWithGoogle,
    logout,
  } = useAuth()

  // ========================================================================
  // 핸들러
  // ========================================================================
  const handleLoginWithGoogle = async () => {
    const result = await loginWithGoogle()
    if (result.success) {
      console.log('[MainLayout] Google 로그인 성공')
    }
  }

  const handleLogout = async () => {
    const success = await logout()
    if (success) {
      console.log('[MainLayout] 로그아웃 성공')
    }
  }

  const handleRefresh = () => {
    // Firestore 실시간 구독 기반이므로 페이지 리로드로 구독 재연결
    console.log('[MainLayout] 페이지 새로고침')
    window.location.reload()
  }

  // ========================================================================
  // 렌더링
  // ========================================================================
  return (
    <div className={styles.layout}>
      {/* ================================================================
          오프라인 인디케이터 (상단 고정)
          ================================================================ */}
      <OfflineIndicator />

      {/* ================================================================
          메인 컨테이너
          ================================================================ */}
      <div className={user ? styles.containerWide : styles.containerNarrow}>
        {/* ============================================================
            헤더
            ============================================================ */}
        <Header
          user={user}
          isLoading={isLoading}
          onLoginWithGoogle={handleLoginWithGoogle}
          onLogout={handleLogout}
          onRefresh={handleRefresh}
        />

        {/* ============================================================
            메인 콘텐츠
            - 로그인된 경우에만 표시
            ============================================================ */}
        <main className={user ? styles.mainTop : styles.mainCentered}>
          {user ? (
            children
          ) : (
            <div className={styles.loginPrompt}>
              <p>로그인하시면 글을 작성할 수 있습니다.</p>
            </div>
          )}
        </main>

        {/* ============================================================
            푸터 (선택적)
            ============================================================ */}
        {showFooter && (
          <footer className={styles.footer}>
            <p>
              500자 글 작성기 - React Migration v3.0
            </p>
          </footer>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// 기본 export
// ============================================================================
export default MainLayout
